// MAJLIS-140 (P-39, consultant ruling d; PROPOSED 2026-09-29): what the browser did to a PASSIVE seat's microphone,
// reported ONCE per seat so the phase-1 echo question can be read against it: the four MediaStreamTrack.getSettings()
// booleans (echoCancellation, noiseSuppression, autoGainControl, and voiceIsolation where the browser reports it; null
// where it reports none), the device class and the browser family. NOTHING ELSE (ruling d): the user-agent string is
// read HERE, on the phone, only to pick one of a few fixed words, and never leaves it; no device, track or group id.
import type { MicSettingsData } from "../protocol/types";

type Settings = Partial<Record<"echoCancellation" | "noiseSuppression" | "autoGainControl" | "voiceIsolation", unknown>>;

function flag(v: unknown): boolean | null {
  return typeof v === "boolean" ? v : null;
}

/** iOS/iPadOS, Android or anything else; `iOS` from the app's own detectIOS (iPadOS reports a desktop UA). */
export function deviceClass(ua: string, isIOS: boolean): MicSettingsData["device"] {
  if (isIOS) return "ios";
  return /Android/i.test(ua) ? "android" : "desktop";
}

/** The browser family, from a fixed list; the order matters (every Chromium names Chrome and Safari too). */
export function browserFamily(ua: string): MicSettingsData["browser"] {
  if (/SamsungBrowser\//.test(ua)) return "samsung";
  if (/Edg(e|A|iOS)?\//.test(ua)) return "edge";
  if (/(Firefox|FxiOS)\//.test(ua)) return "firefox";
  if (/(Chrome|CriOS|Chromium)\//.test(ua)) return "chrome";
  if (/Safari\//.test(ua)) return "safari";
  return "other";
}

export function micSettingsOf(
  track: Pick<MediaStreamTrack, "getSettings">,
  ua: string,
  isIOS: boolean,
): MicSettingsData {
  let s: Settings;
  try {
    s = track.getSettings() as Settings;
  } catch {
    s = {}; // a browser without getSettings reports nothing: every boolean is null
  }
  return {
    echo_cancellation: flag(s.echoCancellation),
    noise_suppression: flag(s.noiseSuppression),
    auto_gain_control: flag(s.autoGainControl),
    voice_isolation: flag(s.voiceIsolation),
    device: deviceClass(ua, isIOS),
    browser: browserFamily(ua),
  };
}
